/**
 * lazyWithRetry — React.lazy with one delayed retry for chunk-load failures.
 *
 * Why this exists: hashed chunks can 404 or truncate on a deploy race (the HTML
 * references a chunk set that a mid-session deploy just replaced) or a network
 * blip. A rejected lazy import() propagates PAST Suspense to the nearest Error
 * Boundary and takes the whole app to the error fallback — a heavy outcome for
 * a transient fetch failure. One retry after a short delay absorbs blips; a
 * genuine outage still rejects and reaches the AppInitWrapper ErrorBoundary,
 * which reports RootErrorBoundary.Encounter to monitoring.
 *
 * Deliberately NOT here: cache-busting query strings (defeats CDN caching and
 * cannot fix a truly purged chunk) and location.reload() (risks a reload loop
 * if the failure is persistent).
 */
import { lazy, ComponentType, LazyExoticComponent } from 'react';

const RETRY_DELAY_MS = 1500;

// ComponentType<any> mirrors React.lazy's own constraint: under strictFunctionTypes,
// ComponentType<unknown> rejects every component whose props are narrower than
// unknown (i.e. all of them). eslint's no-explicit-any is disabled for the same
// reason React's typings use `any` here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    importer().catch(
      (firstError: unknown) =>
        new Promise<{ default: T }>((resolve, reject) => {
          setTimeout(() => {
            importer().then(resolve, () => reject(firstError));
          }, RETRY_DELAY_MS);
        }),
    ),
  );
}
